import React, { Fragment } from "react";

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";

import { fireOnce, formatDistance } from "@ds-developer/frontend";
import { useTealium } from "@hubcapp/context";

import { AreaPricesCardProps } from "./types";
import Skeleton from "./components/Skeleton";

const useStyles = makeStyles(theme => ({
  headerContent: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",

    [theme.breakpoints.down("xs")]: {
      "& .MuiTypography-body2": {
        textAlign: "right",
      },
    },
  },
  content: {
    padding: "0 !important",
  },
  container: {
    padding: theme.spacing(3, 2, 3, 2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  textField: {
    flex: 1,

    [theme.breakpoints.down("xs")]: {
      flex: 1,
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
  changeValueButton: {
    border: `1px solid ${theme.palette.secondary.main}`,
    borderRadius: "4px",
    height: "56px",
    width: "56px",
    marginLeft: theme.spacing(2),

    "&:disabled": {
      borderColor: theme.palette.action.disabled,
    },

    [theme.breakpoints.down("xs")]: {
      marginTop: theme.spacing(1),
    },
  },
  valueChangeIcon: {
    color: theme.palette.primary.main,

    "&.disabled": {
      color: theme.palette.action.disabled,
    },
  },
  helpText: {
    flex: 1,
    textAlign: "left",
    padding: theme.spacing(1, 3, 1, 3),

    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1, 0),
    },
  },
}));

const AreaPricesCard: React.FC<AreaPricesCardProps> = ({
  lastChanged,
  prices,
  editable,
  loading = false,
  cardTitle,
  handleChange,
  memoizedPrices,
}) => {
  const classes = useStyles();
  const { trackEvent } = useTealium();

  return (
    <Card data-test="area-prices-card">
      <CardHeader
        classes={{ content: classes.headerContent }}
        title={!loading && cardTitle}
        subheader={
          lastChanged &&
          `Last changed ${formatDistance(lastChanged, new Date(Date.now()), {
            addSuffix: true,
          })}`
        }
        subheaderTypographyProps={{ variant: "body2" }}
      />
      {loading ? (
        <Skeleton />
      ) : (
        <CardContent className={classes.content}>
          {prices?.map((price, idx) => (
            <Fragment key={idx}>
              <Divider />
              <Grid container className={classes.container}>
                <Grid
                  container
                  item
                  xl={3}
                  lg={4}
                  md={4}
                  sm={7}
                  xs={12}
                  direction="row"
                >
                  {/*
                  Could be replaced by the CurrencyField component in https://github.com/aacom/ds-lib-chassis/pull/73
                  Haven't refactored for the moment due to MVP time constraints
                  */}
                  <TextField
                    label={price.label}
                    error={price.error}
                    name={price.name}
                    onChange={({ target }) =>
                      handleChange({
                        ...price,
                        value: target.value,
                      })
                    }
                    onClick={() => {
                      fireOnce(
                        `Areas & prices - Edit My District|Interaction|${price.label}`,
                        () => {
                          trackEvent({
                            eventCategory: "Areas & prices - Edit My District",
                            eventAction: "Interaction",
                            eventLabel: price.label,
                          });
                        },
                      );
                    }}
                    value={price.value}
                    variant="outlined"
                    type="text"
                    disabled={!editable}
                    className={classes.textField}
                    InputProps={{
                      startAdornment: <Box mr={1}>£</Box>,
                    }}
                    inputProps={{
                      ["data-test"]: `input-${price.name}`,
                    }}
                    helperText={price.errorMessage}
                  />
                </Grid>
                <Grid item xl={9} lg={8} md={8} sm={5} xs={12}>
                  <Typography className={classes.helpText}>
                    {price.useMemo
                      ? memoizedPrices[idx].helperText
                      : price.helperText}
                  </Typography>
                </Grid>
              </Grid>
            </Fragment>
          ))}
        </CardContent>
      )}
    </Card>
  );
};

export default AreaPricesCard;
